import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  ContactFormMessagesFilterParams,
  ContactFormMessageType,
  ContactFormMessagesListResponse
} from '@/types/contact-message'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import {
  fetchContactFormMessages,
  fetchContactFormMessage,
  deleteContactFormMessage,
  bulkDeleteContactFormMessages
} from '@/lib/api'
import { SortEnum } from '@/enums'

type ContactFormMessageStateType = {
  isLoading: boolean
  response: ContactFormMessageType | null
  error: {
    status?: number
    message: string
  } | null
}

type ContactFormMessagesListStateType = {
  isLoading: boolean
  response: ContactFormMessagesListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type ContactFormMessagesStoreState = {
  filters: ContactFormMessagesFilterParams
  setFilters: (filters: Partial<ContactFormMessagesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  contactFormMessagesListState: ContactFormMessagesListStateType
  fetchContactFormMessages: (params?: ContactFormMessagesFilterParams) => Promise<ContactFormMessagesListResponse>
  resetContactFormMessagesList: () => void

  contactFormMessageState: ContactFormMessageStateType
  getContactFormMessage: (id: string) => Promise<ContactFormMessageType>
  deleteContactFormMessage: (id: string) => Promise<void>
  resetContactFormMessage: () => void

  selectedContactFormMessageIds: string[]
  selectContactFormMessage: (id: string) => void
  deselectContactFormMessage: (id: string) => void
  toggleContactFormMessageSelection: (id: string) => void
  selectAllContactFormMessages: (items: ContactFormMessageType[]) => void
  deselectAllContactFormMessages: () => void
  isContactFormMessageSelected: (id: string) => boolean

  bulkDeleteContactFormMessages: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: ContactFormMessagesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialContactFormMessageState: ContactFormMessageStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialContactFormMessagesListState: ContactFormMessagesListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useContactFormMessagesStore = create<ContactFormMessagesStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    contactFormMessageState: initialContactFormMessageState,
    contactFormMessagesListState: initialContactFormMessagesListState,
    selectedContactFormMessageIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof ContactFormMessagesFilterParams]
          } else {
            updatedFilters[key as keyof ContactFormMessagesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: ContactFormMessagesFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as ContactFormMessagesFilterParams['sort']
            break
          case 'sortBy':
            filters[key] = value as ContactFormMessagesFilterParams['sortBy']
            break
          case 'read':
            filters[key] = value === 'true'
            break
          default:
            filters[key as keyof ContactFormMessagesFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, value.toString())
        }
      })

      return params
    },

    fetchContactFormMessages: async (params?: ContactFormMessagesFilterParams) => {
      const currentState = get().contactFormMessagesListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        contactFormMessagesListState: {
          ...initialContactFormMessagesListState,
          isLoading: true
        }
      }))

      try {
        const filtersToUse = params || get().filters
        const data = await fetchContactFormMessages(filtersToUse)

        set(() => ({
          contactFormMessagesListState: {
            ...initialContactFormMessagesListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          contactFormMessagesListState: {
            ...initialContactFormMessagesListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetContactFormMessagesList: () => {
      set(() => ({ contactFormMessagesListState: initialContactFormMessagesListState }))
    },

    getContactFormMessage: async (id: string) => {
      const currentState = get().contactFormMessageState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        contactFormMessageState: {
          ...initialContactFormMessageState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchContactFormMessage(id)

        set(() => ({
          contactFormMessageState: {
            ...initialContactFormMessageState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          contactFormMessageState: {
            ...initialContactFormMessageState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteContactFormMessage: async (id: string) => {
      set(() => ({
        contactFormMessageState: {
          ...get().contactFormMessageState,
          isLoading: true
        }
      }))

      try {
        await deleteContactFormMessage(id)

        set(() => ({ contactFormMessageState: initialContactFormMessageState }))
      } catch (error: any) {
        set(() => ({
          contactFormMessageState: {
            ...initialContactFormMessageState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetContactFormMessage: () => {
      set(() => ({ contactFormMessageState: initialContactFormMessageState }))
    },

    selectContactFormMessage: id => {
      set(state => {
        if (state.selectedContactFormMessageIds.includes(id)) {
          return state
        }
        return { selectedContactFormMessageIds: [...state.selectedContactFormMessageIds, id] }
      })
    },

    deselectContactFormMessage: id => {
      set(state => ({
        selectedContactFormMessageIds: state.selectedContactFormMessageIds.filter(i => i !== id)
      }))
    },

    toggleContactFormMessageSelection: id => {
      set(state => {
        if (state.selectedContactFormMessageIds.includes(id)) {
          return { selectedContactFormMessageIds: state.selectedContactFormMessageIds.filter(i => i !== id) }
        }
        return { selectedContactFormMessageIds: [...state.selectedContactFormMessageIds, id] }
      })
    },

    selectAllContactFormMessages: items => {
      set(() => ({ selectedContactFormMessageIds: items.map(i => i.id) }))
    },

    deselectAllContactFormMessages: () => {
      set(() => ({ selectedContactFormMessageIds: [] }))
    },

    isContactFormMessageSelected: id => {
      return get().selectedContactFormMessageIds.includes(id)
    },

    bulkDeleteContactFormMessages: async ids => {
      set(state => ({
        contactFormMessageState: {
          ...state.contactFormMessageState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteContactFormMessages({ ids })

        set(state => ({
          contactFormMessageState: {
            ...state.contactFormMessageState,
            isLoading: false
          },
          selectedContactFormMessageIds: state.selectedContactFormMessageIds.filter(
            id => !response.deletedItems.includes(id)
          )
        }))

        return response
      } catch (error: any) {
        set(state => ({
          contactFormMessageState: {
            ...state.contactFormMessageState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))
        throw error
      }
    }
  }))
)

export default useContactFormMessagesStore
